import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Loader2, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CardView } from "@/components/card/CardView";
import { API, trackEvent } from "@/lib/api";
import { THEMES, downloadVCard, publicUrl } from "@/lib/card";

export default function PublicCard() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [missing, setMissing] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const tracked = useRef(false);

  useEffect(() => {
    axios.get(`${API}/public/cards/${slug}`).then(({ data }) => {
      setCard(data);
      document.title = `${data.company_name || "Scheda"} · Identità Impresa`;
      if (!tracked.current) { tracked.current = true; trackEvent(slug, "view"); }
    }).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center" data-testid="public-card-not-found">
      <h1 className="text-3xl font-bold">Scheda non trovata</h1>
      <p className="text-slate-500">Il link potrebbe essere cambiato.</p>
      <Button asChild className="rounded-full bg-blue-600"><Link to="/">Vai a Identità Impresa</Link></Button>
    </div>
  );
  if (!card) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin text-blue-600" /></div>;

  const url = publicUrl(card.slug);
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: card.company_name, url }); return; } catch { /* fall back to QR */ }
    }
    setQrOpen(true);
  };
  const vcard = () => { downloadVCard(card); trackEvent(slug, "vcard"); };

  return (
    <div className="min-h-screen" style={{ background: (THEMES[card.theme] || THEMES.classic_executive).bg }} data-testid="public-card-page">
      <div className="mx-auto max-w-md sm:py-10">
        <div className="sm:overflow-hidden sm:rounded-3xl sm:shadow-2xl">
          <CardView card={card} onEvent={(type, target) => trackEvent(slug, type, target)} onVCard={vcard} onShare={share} />
        </div>
      </div>
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-xs" data-testid="public-card-qr-dialog">
          <DialogHeader><DialogTitle>Condividi la scheda</DialogTitle></DialogHeader>
          <div className="flex justify-center rounded-xl border p-4"><QRCodeCanvas value={url} size={200} marginSize={1} /></div>
          <Button variant="outline" data-testid="public-card-copy-link" onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Link copiato"))}>
            <Copy size={16} className="mr-2" /> Copia link
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
